<script setup lang="ts">
  import { useFlightStore } from '@/components/fsf/stores/flight.store'
  import { useFsfStore } from '@/components/fsf/stores/fsf.store'
  import { ISO_YEAR_AND_MONTH_FORMAT } from '@/utils/constants/date'
  import { alternativeDayjsFormat } from '@/utils/dayjs/utils'

  const dayjs = useDayjs()

  const flightStore = useFlightStore()
  const fsfStore = useFsfStore()
  const modelValue = defineModel<string>()

  const monthList = computed(() => {
    const { startDate, endDate } = fsfStore.currentDateRange
    const months = []
    let date = dayjs(startDate)

    while (date.isBefore(endDate, 'month')) {
      months.push(date.format(ISO_YEAR_AND_MONTH_FORMAT))
      date = date.add(1, 'month')
    }

    const nextYear = dayjs().add(1, 'year').format('YYYY')
    const nextYearStartingIndex = months.findIndex((month) => month.includes(nextYear))

    const monthItems = months.map((date) => ({
      key: date,
      displayDate: alternativeDayjsFormat(date, 'MMM'),
      splitter: false
    }))
    monthItems.splice(nextYearStartingIndex, 0, { splitter: true, key: nextYear, displayDate: nextYear })

    return monthItems
  })

  const prices = computed(() => {
    const pricingMap: {
      [key: string]: number
    } = {}

    flightStore.monthPricing[fsfStore.currentFlightType].forEach((mp) => {
      pricingMap[mp.key] = mp.price === null ? NaN : mp.price ?? 0
    })

    return pricingMap
  })

  const hasDirect = computed(() => {
    const hasDirect: {
      [key: string]: boolean
    } = {}

    flightStore.monthPricing[fsfStore.currentFlightType].forEach((mp) => {
      hasDirect[mp.key] = mp.hasDirect
    })

    return hasDirect
  })

  const hasFlight = (monthKey: string) => {
    return (
      flightStore.monthPricing[fsfStore.currentFlightType]?.some((mp) => mp.key === monthKey && mp.hasFlight) ?? false
    )
  }

  const setActiveMonth = () => {
    if (fsfStore.selectedMonth !== dayjs().format(ISO_YEAR_AND_MONTH_FORMAT)) {
      return
    }
    if (!monthList.value.length || !Object.values(prices.value).length) {
      return
    }

    for (const month of monthList.value) {
      if (!isNaN(prices.value[month.key])) {
        fsfStore.selectedMonth = month.key
        break
      }
    }
  }

  setActiveMonth()

  const { isDesktop } = useBTBreakpoints()

  const scrollToMenuItem = () => {
    const menuItem = document.getElementById(`menu-item-${modelValue.value}`)
    if (menuItem) {
      menuItem.scrollIntoView({ block: 'center', behavior: 'instant', inline: 'center' })
    }
  }

  onMounted(() => {
    if (!modelValue.value || isDesktop.value) {
      return
    }

    scrollToMenuItem()
  })

  watch(
    () => modelValue.value,
    () => {
      if (isDesktop.value) {
        return
      }

      scrollToMenuItem()
    }
  )

  watch(
    () => prices.value,
    () => {
      setActiveMonth()
    }
  )
</script>

<template>
  <RadioGroupRoot
    v-model="modelValue"
    as="nav"
    class="grid grid-flow-col gap-2 overflow-x-auto p-4 md:p-0 lg:px-0"
    :aria-label="$t('fsf.monthMenu.selectMonth')"
  >
    <template
      v-for="month in monthList"
      :key="month.key"
    >
      <div
        v-if="month.splitter"
        class="relative box-content flex w-10 items-center justify-center border border-transparent text-center"
      >
        <div class="splitter" />
        <span class="bg-white text-xs leading-5 text-secondary">
          {{ month.displayDate }}
        </span>
      </div>

      <RadioGroupItem
        v-else
        :id="`menu-item-${month.key}`"
        class="flex h-24 w-14 flex-col items-center gap-2 rounded border py-3 md:h-26 md:w-16 md:gap-3 lg:h-27 lg:w-21 lg:gap-3 lg:py-4"
        :class="{
          'border-transparent bg-bt-green': month.key === modelValue,
          'border-border ': month.key !== modelValue,
          '!h-auto': fsfStore.isBusinessClass
        }"
        :aria-label="month.displayDate"
        :value="month.key"
      >
        <div class="text-subtitle-2">{{ month.displayDate }}</div>
        <div v-if="flightStore.isMonthPricingPending">...</div>
        <template v-else>
          <div
            v-if="Object.values(prices).length && isNaN(prices[month.key]) && !hasFlight(month.key)"
            :class="{
              'text-blue-dark': month.key === modelValue,
              'text-border': month.key !== modelValue
            }"
          >
            <Icon
              name="fsf/airplane-inactive"
              size="sm"
            />
          </div>
          <div
            v-else-if="fsfStore.isOutboundFlightType && !fsfStore.hideFsfPrices"
            class="flex flex-col items-center gap-1 font-medium"
          >
            <FsfPrice
              v-if="prices[month.key] && fsfStore.showPrices"
              :price="prices[month.key]"
            />
            <Icon
              v-if="hasDirect[month.key]"
              name="common/round-airplane-90-deg"
              size="sm"
            />
          </div>
        </template>
      </RadioGroupItem>
    </template>
  </RadioGroupRoot>
</template>

<style scoped>
  .splitter:after {
    content: '';
    background-image: linear-gradient(theme('colors.border') 33%, hsla(0, 0%, 100%, 0) 0);
    background-size: 1px 10px;
    background-repeat: repeat-y;
    position: absolute;
    width: 1px;
    height: 100%;
    top: 0;
    left: 50%;
  }
</style>
